/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-nocheck
/**
 * DeltaChip — shows how a value compares to a baseline (county avg,
 * parent scope, peer median). Renders ↑/↓/● with a signed delta and
 * colors the arrow green/red based on "higher is better" vs actual.
 *
 * The component supports either:
 *   - a single baseline (simplest common case), or
 *   - multiple baselines (e.g. vs county + vs LD + vs peer median) —
 *     the primary baseline is rendered inline, the others appear in
 *     a hover tooltip so the UI stays uncluttered.
 *
 *   <DeltaChip value={62} baseline={48} />
 *   <DeltaChip value={62} baselines={[
 *     { label: 'vs county', value: 48 },
 *     { label: 'vs LD',     value: 55 },
 *     { label: 'peer med',  value: 50 },
 *   ]} />
 *
 * A `format` prop controls unit rendering: 'num' for raw numbers (default),
 * 'pct' for percentages (appends %), 'pp' for percentage points (appends pp).
 */
import React, { useEffect, useRef, useState } from 'react';

export type DeltaBaseline = { label: string; value: number };
export type DeltaFormat = 'num' | 'pct' | 'pp';
export type DeltaDirection = 'higher-better' | 'lower-better';

type Props = {
  value: number | null | undefined;
  /** Simple single-baseline form. */
  baseline?: number | null;
  /** Multi-baseline form (wins over `baseline` if both are set). */
  baselines?: DeltaBaseline[];
  /** Default label when using the single-baseline form. */
  label?: string;
  format?: DeltaFormat;
  direction?: DeltaDirection;
  /** Omit to use a sensible default — renders inline-flex. */
  className?: string;
  /** Hide the baseline label ("vs county") — useful in tight table cells. */
  compact?: boolean;
};

function formatDelta(delta: number, format: DeltaFormat): string {
  const sign = delta > 0 ? '+' : delta < 0 ? '' : '±';
  const abs = Math.abs(delta);
  const digits = abs < 10 ? 1 : 0;
  const numeric = (delta < 0 ? '-' : sign) + abs.toFixed(digits);
  if (format === 'pct') return `${numeric}%`;
  if (format === 'pp') return `${numeric}pp`;
  return numeric;
}

function arrowFor(delta: number, direction: DeltaDirection): string {
  if (Math.abs(delta) < 0.05) return '●';
  const up = delta > 0;
  if (direction === 'higher-better') return up ? '↑' : '↓';
  return up ? '↓' : '↑';
}

function colorFor(
  delta: number,
  direction: DeltaDirection,
): { bg: string; text: string; border: string } {
  if (Math.abs(delta) < 0.05) {
    return {
      bg: 'bg-bg-elevated/40',
      text: 'text-fg-secondary',
      border: 'border-border-subtle/60',
    };
  }
  const good =
    direction === 'higher-better' ? delta > 0 : delta < 0;
  return good
    ? {
        bg: 'bg-status-success-bg/15',
        text: 'text-status-success',
        border: 'border-status-success-bg/40',
      }
    : {
        bg: 'bg-status-danger-bg/15',
        text: 'text-status-danger',
        border: 'border-status-danger-bg/40',
      };
}

export const DeltaChip: React.FC<Props> = ({
  value,
  baseline,
  baselines,
  label = 'vs baseline',
  format = 'num',
  direction = 'higher-better',
  className = '',
  compact = false,
}) => {
  const rows: DeltaBaseline[] =
    baselines && baselines.length > 0
      ? baselines
      : baseline != null
      ? [{ label, value: baseline }]
      : [];

  if (rows.length === 0 || value == null || Number.isNaN(value)) return null;

  const primary = rows[0];
  const primaryDelta = value - primary.value;
  const { bg, text, border } = colorFor(primaryDelta, direction);
  const arrow = arrowFor(primaryDelta, direction);

  const [hover, setHover] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const anchorRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (!hover) {
      setPos(null);
      return;
    }
    const a = anchorRef.current;
    if (!a) return;
    const rect = a.getBoundingClientRect();
    setPos({ top: rect.bottom + 6, left: rect.left });
  }, [hover]);

  const hasMulti = rows.length > 1;

  return (
    <span
      ref={anchorRef}
      className={`relative inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-semibold tabular-nums ${bg} ${text} ${border} ${className}`}
      onMouseEnter={() => hasMulti && setHover(true)}
      onMouseLeave={() => hasMulti && setHover(false)}
    >
      <span className="text-xs leading-none">{arrow}</span>
      <span>{formatDelta(primaryDelta, format)}</span>
      {!compact && <span className="text-fg-muted font-normal">{primary.label}</span>}
      {hasMulti && (
        <span className="text-fg-muted font-normal">·</span>
      )}
      {hasMulti && pos && hover && (
        <span
          className="fixed z-50 rounded-md border border-border-base bg-bg-base px-2 py-1 shadow-xl text-[10px] pointer-events-none"
          style={{ top: pos.top, left: pos.left, minWidth: 140 }}
        >
          <div className="text-fg-muted mb-1 uppercase tracking-wide text-[9px]">
            Comparisons
          </div>
          {rows.map((r) => {
            const d = value - r.value;
            const c = colorFor(d, direction);
            return (
              <div
                key={r.label}
                className="flex items-center justify-between gap-3 py-0.5"
              >
                <span className="text-fg-secondary">{r.label}</span>
                <span className={`${c.text} font-semibold`}>
                  {arrowFor(d, direction)} {formatDelta(d, format)}
                </span>
              </div>
            );
          })}
        </span>
      )}
    </span>
  );
};

export default DeltaChip;
